import type { SortDirection } from "@/components/SortableHeader";
import type { Connector, Workspace } from "@/types/api";
import { formatPipeline } from "./pipelineHelpers";
import { hasUnresolvedStage, parseWorkspacePipeline } from "./workspaceHelpers";

// The Pipelines page's Drafts tab (Figma 14894-157315): the saved
// workspaces that aren't running as a job, as rows of name, pipeline flow
// and checkpoint setting.

export type DraftCheckpoint = "enabled" | "off";

export interface DraftRow {
  workspace: Workspace;
  connector: Connector | null;
  /** No saved pipeline, one that doesn't parse, or a stage naming a
   *  service that doesn't exist — the studio graph's per-card rule. */
  unresolved: boolean;
  checkpoint: DraftCheckpoint;
}

export interface DraftFilters {
  name: string;
  checkpoint: DraftCheckpoint[];
}

// Sortable columns (the actions column is excluded), the Deployed table's
// keys: the name, the pipeline by its source (service.resource — the
// flow's natural key; middleware/sink can't be meaningfully sorted by one
// arrow), and the checkpoint setting.
export type DraftSortColumn = "name" | "pipeline" | "checkpoint";
export const DRAFT_SORT_COLUMNS: DraftSortColumn[] = [
  "name",
  "pipeline",
  "checkpoint",
];

export interface DraftSort {
  id: DraftSortColumn;
  desc: boolean;
}

const SORT_ACCESSORS: Record<DraftSortColumn, (row: DraftRow) => string> = {
  name: (row) => row.workspace.name,
  pipeline: (row) => formatPipeline({ pipeline: row.connector }).source,
  checkpoint: (row) => row.checkpoint,
};

/** The Pipelines cycle: first click sorts ascending, second flips to
 *  descending, third clears the sort. */
export function nextDraftSort(
  prev: DraftSort | null,
  id: DraftSortColumn,
): DraftSort | null {
  if (prev?.id !== id) return { id, desc: false };
  return prev.desc ? null : { id, desc: true };
}

export function draftSortDirection(
  sort: DraftSort | null,
  id: DraftSortColumn,
): SortDirection {
  if (sort?.id !== id) return null;
  return sort.desc ? "desc" : "asc";
}

/** A stored sort, if it still names a sortable column. */
export function parseDraftSort(value: unknown): DraftSort | null {
  if (typeof value !== "object" || value === null) return null;
  const sort = value as Record<string, unknown>;
  if (!DRAFT_SORT_COLUMNS.includes(sort.id as DraftSortColumn)) return null;
  if (typeof sort.desc !== "boolean") return null;
  return { id: sort.id as DraftSortColumn, desc: sort.desc };
}

/** The workspaces that are drafts: the ones with no job under their name.
 *  A job carries its workspace's name (deploy names the job after the
 *  workspace, importing a job names the workspace after it), so a job
 *  under the name means the workspace IS a deployed pipeline — the same
 *  fold the Studio's Open-pipeline list applies. */
export function draftWorkspaces(
  workspaces: ReadonlyArray<Workspace>,
  jobs: ReadonlyArray<{ name: string }>,
): Workspace[] {
  const deployed = new Set(jobs.map((job) => job.name));
  return workspaces.filter((ws) => !deployed.has(ws.name));
}

export function draftCheckpoint(connector: Connector | null): DraftCheckpoint {
  return connector?.checkpoint?.enabled ? "enabled" : "off";
}

/** Draft rows; `serviceNames` null (services not loaded) skips the
 *  unresolved-service check rather than flagging everything. */
export function draftRows(
  workspaces: ReadonlyArray<Workspace>,
  serviceNames: Set<string> | null,
): DraftRow[] {
  return workspaces.map((workspace) => {
    const connector = parseWorkspacePipeline(workspace.pipeline);
    return {
      workspace,
      connector,
      unresolved: isUnresolved(connector, serviceNames),
      checkpoint: draftCheckpoint(connector),
    };
  });
}

function isUnresolved(
  connector: Connector | null,
  serviceNames: Set<string> | null,
): boolean {
  if (!connector) return true;
  return serviceNames !== null && hasUnresolvedStage(connector, serviceNames);
}

/** The rows the filters let through, in `sort` order (list order when
 *  nothing is sorted). Same semantics as the Deployed table: the name
 *  matches as a case-insensitive substring, an empty multi-select matches
 *  everything. */
export function visibleDrafts(
  rows: ReadonlyArray<DraftRow>,
  filters: DraftFilters,
  sort: DraftSort | null,
): DraftRow[] {
  const needle = filters.name.trim().toLowerCase();
  const visible = rows.filter(
    (row) =>
      row.workspace.name.toLowerCase().includes(needle) &&
      (filters.checkpoint.length === 0 ||
        filters.checkpoint.includes(row.checkpoint)),
  );
  if (!sort) return visible;
  return visible.sort((a, b) => compareDrafts(a, b, sort));
}

function compareDrafts(a: DraftRow, b: DraftRow, sort: DraftSort): number {
  const accessor = SORT_ACCESSORS[sort.id];
  const cmp = accessor(a).localeCompare(accessor(b), undefined, {
    sensitivity: "base",
  });
  return sort.desc ? -cmp : cmp;
}
